/**
 * ERC Unannotated Validator Adapter for DRC System
 *
 * This validator checks for components that have not been annotated with a reference designator.
 * It excludes non-component elements like ground symbols, power symbols, labels, and portals,
 * as well as components that are excluded from both BOM and PCB.
 *
 * Feature-gated via DrcEnabledCheckers.ErcUnannotatedValidator
 */
import { defaultPropertyDefinitions, portalPartType } from "@buildwithflux/constants";
import { EditorModes, getElementPartCategoryProperty, isGroundElement, isSystemPart, ReviewType, } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
/**
 * Checks if a reference designator is unannotated (contains ? or is empty).
 */
function isUnannotated(designator) {
    if (!designator)
        return true;
    return designator.includes("?") || designator.trim() === "";
}
/**
 * Checks if an element is excluded from the BOM based on its properties.
 */
function isExcludedFromBOM(properties) {
    if (!properties)
        return false;
    const prop = Object.values(properties).find((p) => p.name === defaultPropertyDefinitions.exclude_from_bom.label);
    if (!prop)
        return false;
    const value = prop.value?.toString();
    return value?.toLowerCase() === "true" || value === "1";
}
/**
 * Checks if an element is excluded from the PCB based on its properties.
 */
function isExcludedFromPCB(properties) {
    if (!properties)
        return false;
    const prop = Object.values(properties).find((p) => p.name === defaultPropertyDefinitions.exclude_from_pcb?.label);
    if (!prop)
        return false;
    const value = prop.value?.toString();
    return value?.toLowerCase() === "true" || value === "1";
}
/**
 * Validator that checks for unannotated components.
 */
export class ErcUnannotatedValidator {
    reviewType = ReviewType.DesignRuleCheckErcUnannotated;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = false;
    async checkForProblems(inputs) {
        const foundProblems = [];
        for (const [elementUid, element] of Object.entries(inputs.elements)) {
            // Skip system parts (ground, power, labels, etc.)
            if (isSystemPart(element.part_uid)) {
                continue;
            }
            // Skip ground elements
            if (isGroundElement(element)) {
                continue;
            }
            // Skip portal elements (Net Portal, Power Net Portal)
            if (getElementPartCategoryProperty(element)?.value === portalPartType) {
                continue;
            }
            // Skip components that are excluded from both BOM and PCB
            // (these are typically schematic-only symbols that don't need designators)
            if (isExcludedFromBOM(element.properties) && isExcludedFromPCB(element.properties)) {
                continue;
            }
            const designator = element.label;
            if (isUnannotated(designator)) {
                const displayName = element.part_version_data_cache?.name ?? "unknown";
                foundProblems.push({
                    problemTypeKey: this.problemTypeKey,
                    key: `${this.problemTypeKey}_${elementUid}`,
                    label: `Unannotated component: ${displayName}`,
                    affectedItems: [{ type: "element", uid: elementUid }],
                    affectedViews: [EditorModes.schematic],
                });
            }
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcUnannotatedValidator.js.map